/**
 * useStripeCheckout
 * Initiates a Stripe Checkout session for a given item (event ticket, marketplace product, etc.)
 * without requiring the user to be signed in with Firebase or a crypto wallet.
 * On success the browser is redirected to the hosted Stripe Checkout page.
 */
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { apiPost } from "@/lib/api";

const BASE = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";

export interface StripeCheckoutOptions {
  productName: string;
  productType: string;
  amountUsd: number;
  metadata?: Record<string, string>;
}

export function useStripeCheckout() {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  async function startCheckout(opts: StripeCheckoutOptions) {
    if (opts.amountUsd <= 0) {
      toast({ title: "Free Event", description: "No payment required — you're all set!" });
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch(`${BASE}/api/checkout/guest-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(opts),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `Checkout error ${res.status}`);
      }
      const { url } = await res.json() as { url: string };
      // Open in a new tab so the redirect works inside iframes (Replit preview)
      // and on the live site equally. window.location.href is blocked by Stripe's
      // X-Frame-Options: DENY when the app is embedded in an iframe.
      window.open(url, "_blank");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not start checkout";
      toast({ title: "Payment Error", description: msg, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  }

  async function startContentCheckout(productId: 43 | 44) {
    setIsLoading(true);
    try {
      const { url } = await apiPost<{ url: string }>("/api/checkout/content-session", {
        productId,
      });
      window.open(url, "_blank");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not start checkout";
      toast({ title: "Payment Error", description: msg, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  }

  return { startCheckout, startContentCheckout, isLoading };
}
